import { leerToken } from '../utils/sesion';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { STATUS_CONFIG, QUALIFICATION_CONFIG, SOURCE_CONFIG, formatDateShort, getSourceLabel } from '../utils/constants';
import ManualLeadModal from './ManualLeadModal';
import { Star, Trash2, MessageCircle, Search, Download, Plus, Filter } from 'lucide-react';
import api from '../utils/api';
import LeadDetail from './LeadDetail';
import WhatsAppModal from './WhatsAppModal';
import { hasFeature } from '../utils/features';

export default function LeadTable({ leads, onRefresh, showDatasetCol, total, page, pages, filters, setFilters, datasets }) {
  const { tenant, user } = useAuth();
  const canWhatsApp = hasFeature(tenant, user, 'lead_whatsapp');
  const [selectedLead, setSelectedLead] = useState(null);
  const [waLead, setWaLead] = useState(null);
  const customCols = tenant?.features?.custom_columns || [];
  const [showManual, setShowManual] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ categories: [], cities: [], sources: [], utmCampaigns: [] });
  const datasetId = filters?.dataset_id;

  useEffect(() => {
    api.get('/leads/meta/filters', { params: { dataset_id: datasetId } }).then(res => setFilterOptions(res.data)).catch(() => {});
  }, [datasetId]);

  const exportCSV = () => {
    const token = leerToken();
    const params = new URLSearchParams();
    Object.entries(filters || {}).forEach(([k, v]) => { if (v && k !== 'page' && k !== 'search') params.set(k, v); });
    fetch(`/api/leads/export/csv?${params.toString()}`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => {
        const a = document.createElement('a'); a.href = window.URL.createObjectURL(blob);
        a.download = `leads_${Date.now()}.csv`; document.body.appendChild(a); a.click(); a.remove();
        toast.success('CSV descargado');
      }).catch(() => toast.error('Error al exportar'));
  };
  const extraFilterKeys = ['status', 'qualification', 'city', 'category', 'source', 'utm_campaign'];
  const activeFilterCount = filters ? extraFilterKeys.filter(k => filters[k]).length : 0;

  const getExtraField = (lead, key) => {
    if (!lead.extra_data) return '—';
    try {
      const data = typeof lead.extra_data === 'string' ? JSON.parse(lead.extra_data) : lead.extra_data;
      return data[key] || '—';
    } catch { return '—'; }
  };

  const openLead = (lead) => {
    setSelectedLead(lead);
  };

  return (
    <>
      {filters && setFilters && (<>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12, alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '1 1 200px', maxWidth: 360 }}>
            <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input placeholder="Buscar leads..." value={filters.search || ''} onChange={e => setFilters({ ...filters, search: e.target.value, page: 1 })} style={{ paddingLeft: 36 }} />
          </div>
          <button className={`btn btn-sm ${showFilters ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setShowFilters(!showFilters)}>
            <Filter size={14} /> Filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={exportCSV}><Download size={14} /> CSV</button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowManual(true)}><Plus size={14} /> Agregar lead</button>
        </div>

        {showFilters && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14, padding: 14, background: 'var(--bg-secondary)', borderRadius: 10, border: '1px solid var(--border)' }}>
            <select value={filters.status || ''} onChange={e => setFilters({ ...filters, status: e.target.value, page: 1 })} style={{ width: 170 }}>
              <option value="">Todos los estados</option>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select value={filters.qualification || ''} onChange={e => setFilters({ ...filters, qualification: e.target.value, page: 1 })} style={{ width: 170 }}>
              <option value="">Todas las calificaciones</option>
              {Object.entries(QUALIFICATION_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
            </select>
            <select value={filters.city || ''} onChange={e => setFilters({ ...filters, city: e.target.value, page: 1 })} style={{ width: 160 }}>
              <option value="">Todas las ciudades</option>
              {filterOptions.cities?.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filters.category || ''} onChange={e => setFilters({ ...filters, category: e.target.value, page: 1 })} style={{ width: 160 }}>
              <option value="">Todos los sectores</option>
              {filterOptions.categories?.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filters.source || ''} onChange={e => setFilters({ ...filters, source: e.target.value, page: 1 })} style={{ width: 150 }}>
              <option value="">Todas las fuentes</option>
              {filterOptions.sources?.map(s => <option key={s} value={s}>{SOURCE_CONFIG[s]?.label || s}</option>)}
            </select>
            {filterOptions.utmCampaigns?.length > 0 && (
              <select value={filters.utm_campaign || ''} onChange={e => setFilters({ ...filters, utm_campaign: e.target.value, page: 1 })} style={{ width: 170 }}>
                <option value="">Todas las campañas</option>
                {filterOptions.utmCampaigns.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            <button className="btn btn-ghost btn-sm" onClick={() => setFilters({ ...filters, status: '', qualification: '', city: '', category: '', source: '', utm_campaign: '', page: 1 })}>Limpiar</button>
          </div>
        )}
        {typeof total === 'number' && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>{total} leads encontrados</div>}
      </>)}

      <div className="table-container">
        <table>
          <thead><tr>
            <th style={{width:30}}></th>
            <th>Negocio</th>
            <th>Teléfono</th>
            {showDatasetCol && <th>Base</th>}
            <th>Origen</th>
            <th>Estado</th>
            <th>Calificación</th>
            {customCols.map(c => <th key={c.key}>{c.label}</th>)}
            <th>Ingreso</th>
            <th>Último contacto</th>
            <th style={{width:74}}></th>
          </tr></thead>
          <tbody>
            {leads.length === 0 ? (
              <tr><td colSpan={10 + (showDatasetCol?1:0) + customCols.length} style={{ textAlign:'center', padding:40, color:'var(--text-muted)' }}>No se encontraron leads</td></tr>
            ) : leads.map(lead => {
              const sc = STATUS_CONFIG[lead.status] || {};
              const qc = QUALIFICATION_CONFIG[lead.qualification] || {};
              return (
                <tr key={lead.id} onClick={() => openLead(lead)} style={{ cursor:'pointer', background: selectedLead?.id === lead.id ? 'var(--accent-glow)' : 'transparent' }}>
                  <td onClick={e => { e.stopPropagation(); api.put('/leads/'+lead.id+'/favorite').then(onRefresh); }}>
                    <Star size={14} fill={lead.is_favorite ? '#eab308' : 'none'} color={lead.is_favorite ? '#eab308' : 'var(--text-muted)'} style={{cursor:'pointer'}}/>
                  </td>
                  <td style={{ fontWeight:600, maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{lead.business_name || '—'}</td>
                  <td style={{ fontSize:13 }}>{lead.phone || '—'}</td>
                  {showDatasetCol && <td style={{ fontSize:12, color:'var(--text-muted)' }}>{lead.dataset_name || '—'}</td>}
                  <td><span className="badge" style={{ fontSize:10, background:'rgba(46,125,226,0.12)', color:'var(--accent)' }}>{getSourceLabel(lead)}</span></td>
                  <td><select value={lead.status} onChange={async e => { e.stopPropagation(); try { await api.put('/leads/'+lead.id+'/status', { status: e.target.value }); onRefresh(); } catch {} }} onClick={e => e.stopPropagation()} style={{ background: sc.bg || 'rgba(100,116,139,0.15)', color: sc.color || '#64748b', border:'none', borderRadius:20, padding:'4px 8px', fontSize:11, fontWeight:600, cursor:'pointer' }}>
                    {Object.entries(STATUS_CONFIG).map(([k,v]) => <option key={k} value={k} style={{ background:'var(--bg-card)', color:'var(--text-primary)' }}>{v.label}</option>)}
                  </select></td>
                  <td><select value={lead.qualification} onChange={async e => { e.stopPropagation(); try { await api.put('/leads/'+lead.id+'/qualification', { qualification: e.target.value }); onRefresh(); } catch {} }} onClick={e => e.stopPropagation()} style={{ background: qc.bg || 'rgba(100,116,139,0.15)', color: qc.color || '#64748b', border:'none', borderRadius:20, padding:'4px 8px', fontSize:11, fontWeight:600, cursor:'pointer' }}>
                    {Object.entries(QUALIFICATION_CONFIG).map(([k,v]) => <option key={k} value={k} style={{ background:'var(--bg-card)', color:'var(--text-primary)' }}>{v.label}</option>)}
                  </select></td>
                  {customCols.map(c => <td key={c.key} style={{ fontSize:12, color:'var(--text-secondary)' }}>{getExtraField(lead, c.key)}</td>)}
                  <td style={{ fontSize:12, color:'var(--text-muted)' }}>{formatDateShort(lead.created_at)}</td>
                  <td><input type="date" value={lead.last_contacted_at ? lead.last_contacted_at.split('T')[0] : ''} onChange={async e => { e.stopPropagation(); try { await api.put('/leads/'+lead.id+'/last-contact', { date: e.target.value }); onRefresh(); } catch {} }} onClick={e => e.stopPropagation()} style={{ background:'transparent', border:'1px solid var(--border)', borderRadius:6, padding:'2px 6px', fontSize:11, color:'var(--text-secondary)', width:120, cursor:'pointer' }} /></td>
                  <td><div style={{ display:'flex', gap:2, justifyContent:'flex-end' }}>
                    {canWhatsApp && lead.phone && <button className="btn btn-ghost btn-sm" title="Contactar por WhatsApp" onClick={e => { e.stopPropagation(); setWaLead(lead); }} style={{ color:'#22c55e', padding:4 }}><MessageCircle size={14}/></button>}
                    <button className="btn btn-ghost btn-sm" title="Eliminar" onClick={e => { e.stopPropagation(); if(window.confirm('¿Eliminar?')) api.delete('/leads/'+lead.id).then(onRefresh); }} style={{ color:'var(--danger)', padding:4 }}><Trash2 size={14}/></button>
                  </div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div style={{ display:'flex', justifyContent:'center', gap:8, marginTop:16 }}>
          <button className="btn btn-secondary btn-sm" disabled={page<=1} onClick={() => setFilters({...filters, page: page-1})}>Anterior</button>
          <span style={{ color:'var(--text-muted)', fontSize:13, padding:'6px 12px' }}>Página {page} de {pages} ({total} leads)</span>
          <button className="btn btn-secondary btn-sm" disabled={page>=pages} onClick={() => setFilters({...filters, page: page+1})}>Siguiente</button>
        </div>
      )}

      {selectedLead && (
        <LeadDetail leadId={selectedLead.id} onClose={() => setSelectedLead(null)} onRefresh={() => { onRefresh(); }} />
      )}

      {showManual && <ManualLeadModal datasets={datasets} defaultDatasetId={datasetId} onClose={() => { setShowManual(false); onRefresh(); }} />}

      {waLead && <WhatsAppModal lead={waLead} onClose={() => { setWaLead(null); onRefresh(); }} />}
    </>
  );
}
